import { useEffect, useState } from 'react';

/**
 * useIsOnline — subscribes to the browser's online/offline events
 * and returns true when the tab thinks it has connectivity. Used
 * by ListPanel / FocusedPanel / ListPage to gate WRITES on shared
 * lists when offline (per Walter's decision 2026-05-04: shared lists
 * become read-only when offline, with a banner; own lists stay
 * editable so they can sync on reconnect).
 *
 * Why "shared lists only" vs all writes: solo users editing their
 * own content offline is the whole point of Firestore's offline
 * persistence — writes queue, sync on reconnect, no conflict
 * possible because they're the only writer. Shared lists are the
 * dangerous case: two users editing simultaneously while one is
 * offline produces last-writer-wins conflicts that the soft-lock
 * can't catch (the lock requires a Firestore round-trip to
 * acquire, which fails offline). Easier to disallow it entirely.
 *
 * navigator.onLine is best-effort — the browser sets it from OS
 * connectivity APIs that don't always reflect actual reachability.
 * False positives are mostly fine here (we'd just permit a write
 * that ends up queued by Firestore anyway). False negatives are
 * the bigger concern but rare in practice.
 */
export function useIsOnline() {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);
  return online;
}
